import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core'
import { FormArray, FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms'
import { UsersFacade } from '@galabid/gb-dashboard/user/data-access'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { SelectedAuctionFacade } from 'libs/gb-dashboard/auctions/data-access-selected-auction/src/lib/+state/selected-auction.facade'
import * as UsersActions from 'libs/gb-dashboard/user/data-access/src/lib/+state/users.actions'
import { UserIdentityTypeEnum } from 'libs/gb-dashboard/user/data-access/src/lib/+state/users.models'
import { GbValidators } from 'libs/shared/utils/helpers/src/lib/gb-validators/gb-validators'
import { map } from 'rxjs'

interface IdentityItem {
  identityType: UserIdentityTypeEnum
  control: FormControl
}

@UntilDestroy()
@Component({
  selector: 'gb-invite-user-dialog',
  templateUrl: './gb-invite-user-dialog.component.html',
  styleUrls: ['./gb-invite-user-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class GbInviteUserDialogComponent implements OnInit {
  inviteLoaded$ = this.usersFacade.inviteLoaded$
  auction$ = this.selectedAuctionFacade.auction$
  initialFieldType: UserIdentityTypeEnum = UserIdentityTypeEnum.Email
  userIdentityTypeEnum = UserIdentityTypeEnum

  allowedIdentityType$ = this.auction$.pipe(
    map((auction) => {
      if (!auction) return UserIdentityTypeEnum.Email

      if (auction.allow_email_registration && !auction.allow_phone_registration) {
        return UserIdentityTypeEnum.Email
      }

      if (!auction.allow_email_registration && auction.allow_phone_registration) {
        return UserIdentityTypeEnum.Mobile
      }

      return UserIdentityTypeEnum.Email
    }),
  )

  isBothEmailPhoneAllowed$ = this.selectedAuctionFacade.isBothEmailPhoneAllowed$

  // This will hold the dynamic array of IdentityItem objects
  public identities: IdentityItem[] = []

  inviteUserForm = new FormGroup({
    identities: new FormArray([]),
  })

  constructor(
    private usersFacade: UsersFacade,
    private selectedAuctionFacade: SelectedAuctionFacade,
  ) {}

  get identitiesFormArray() {
    return this.inviteUserForm.get('identities') as FormArray
  }

  ngOnInit(): void {
    this.allowedIdentityType$.pipe(untilDestroyed(this)).subscribe((type) => {
      this.initialFieldType = type
      this.addField() // Call addField() once the type is determined
    })
  }

  getFieldType() {
    return this.allowedIdentityType$.pipe(untilDestroyed(this)).subscribe((type) => type)
  }

  addField() {
    const newControl = new FormControl('', [
      Validators.required,
      this.getValidatorForType(this.initialFieldType), // Use the stored value here
    ])

    this.identities.push({
      identityType: this.initialFieldType, // Set the initial type here
      control: newControl,
    })

    this.identitiesFormArray.push(newControl)
  }

  deleteField(index: number) {
    this.identities.splice(index, 1)
    this.identitiesFormArray.removeAt(index)
  }

  submitForm() {
    this.inviteUserForm.valid ? this.submitData() : this.inviteUserForm.markAllAsTouched()
  }

  private getValidatorForType(type: UserIdentityTypeEnum): ValidatorFn {
    return type === UserIdentityTypeEnum.Email ? Validators.email : GbValidators.mobileNumber()
  }

  // A new method to toggle the type
  toggleIdentityType(index: number) {
    const identity = this.identities[index]
    const newType =
      identity.identityType === UserIdentityTypeEnum.Email ? UserIdentityTypeEnum.Mobile : UserIdentityTypeEnum.Email

    // Update the type and validators
    identity.identityType = newType
    identity.control.setValue('')
    identity.control.setValidators([Validators.required, this.getValidatorForType(newType)])
    identity.control.updateValueAndValidity()
  }

  submitData() {
    const values = this.identitiesFormArray.value

    const emails = values
      .map((value: string, index: number) => ({ value, type: this.identities[index].identityType }))
      .filter((item: any) => item.type === UserIdentityTypeEnum.Email)
      .map((item: any) => item.value)

    const mobile_phones = values
      .map((value: string, index: number) => ({ value, type: this.identities[index].identityType }))
      .filter((item: any) => item.type === UserIdentityTypeEnum.Mobile)
      .map((item: any) => item.value)

    if (emails.length > 0) {
      this.submitEmails(emails)
    }

    if (mobile_phones.length > 0) {
      this.submitPhones(mobile_phones)
    }
  }

  submitEmails(emails: string[]) {
    this.usersFacade.dispatch(UsersActions.inviteUsersEmail({ emails }))
  }

  submitPhones(phone_numbers: string[]) {
    this.usersFacade.dispatch(UsersActions.inviteUsersPhoneNumber({ phone_numbers }))
  }

  getLinkTranslate(identityType: string): string {
    return identityType === UserIdentityTypeEnum.Mobile ? 'USERS.CREATE_USER.USE_EMAIL' : 'USERS.CREATE_USER.USE_PHONE'
  }
  // A new getter that returns the FormControl at a specific index
  getControl(index: number): FormControl {
    return this.identitiesFormArray.at(index) as FormControl
  }
}
